import React, { useEffect, useState } from 'react';
import TopBar from '../components/TopBar';
import UploadMappingWizard from '../components/UploadMappingWizard';
import { useUser } from '../context/UserContext';
import { fetchInventoryCountrySummary } from '../components/api';
import CountryPicker from '../components/CountryPicker';

const Inventories = ({ userClaims }) => {
  const [activeTab, setActiveTab] = useState('introduction');
  const [summary, setSummary] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const { user } = useUser();
  const [selectedCountryOption, setSelectedCountryOption] = useState(null);
  const [selectedIso3, setSelectedIso3] = useState('');

  // Auto-select if only one country is available for the user
  useEffect(() => {
    if (!user) return;
    const names = Array.isArray(user.allowedCountries) ? user.allowedCountries : [];
    const codes = Array.isArray(user.allowedCountriesIso3) ? user.allowedCountriesIso3 : [];
    if (names.length === 1 && codes.length === 1) {
      const opt = {
        value: String(codes[0] || '').toLowerCase(),
        label: String(names[0] || ''),
        iso3: String(codes[0] || '').toUpperCase(),
        raw: { Country_Name: names[0], iso3: codes[0] }
      };
      setSelectedCountryOption(opt);
      setSelectedIso3(opt.iso3);
    }
  }, [user]);

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      setLoading(true);
      setError('');
      try {
        if (!selectedIso3) {
          if (mounted) setSummary({});
          return;
        }
        const data = await fetchInventoryCountrySummary();
        const items = Array.isArray(data) ? data : [];

        // Group rows by country iso3 (no filtering)
        const grouped = items.reduce((acc, row) => {
          const iso3 = String(row.iso3 || row.country_iso3 || '').toUpperCase();
          if (!iso3) return acc;
          if (!acc[iso3]) acc[iso3] = [];
          acc[iso3].push(row);
          return acc;
        }, {});

        // Sort categories within each country: regular, then UNCAT, then TOTAL
        Object.keys(grouped).forEach(k => {
          grouped[k].sort((a, b) => {
            const rank = (code) => code === 'TOTAL' ? 2 : (code === 'UNCAT' ? 1 : 0);
            const ra = rank(a.pqs_category_code);
            const rb = rank(b.pqs_category_code);
            if (ra !== rb) return ra - rb;
            return String(a.pqs_category_code || '').localeCompare(String(b.pqs_category_code || ''));
          });
        });

        // Keep only the selected country
        const onlySelected = selectedIso3 ? { [selectedIso3]: grouped[selectedIso3] || [] } : {};
        if (mounted) setSummary(onlySelected);
      } catch (e) {
        if (mounted) setError('Failed to load summary');
      } finally {
        if (mounted) setLoading(false);
      }
    };
    load();
    return () => { mounted = false; };
  }, [user, selectedIso3]);
  

  return (
    <div className="min-h-screen bg-gray-100">
      <TopBar currentPage="inventories" userClaims={userClaims} />
      
      <div className="p-4">
        <div className="bg-white shadow-md rounded-lg p-6">
          <div className="mb-6 border-b border-gray-200">
            <nav className="flex space-x-4" aria-label="Tabs">
              <button
                className={`px-3 py-2 font-medium text-sm rounded-t focus:outline-none ${activeTab === 'introduction' ? 'bg-blue-100 text-blue-700' : 'text-gray-500 hover:text-blue-700'}`}
                onClick={() => setActiveTab('introduction')}
              >
                Introduction
              </button>
              <button
                className={`px-3 py-2 font-medium text-sm rounded-t focus:outline-none ${activeTab === 'upload' ? 'bg-blue-100 text-blue-700' : 'text-gray-500 hover:text-blue-700'}`}
                onClick={() => setActiveTab('upload')}
              >
                Upload & Mapping
              </button>
            </nav>
          </div>
          {activeTab === 'introduction' && (
            <div className="p-6">
              <div className="mb-4">
                <h3 className="text-lg font-semibold mb-2">Cold Chain Inventories</h3>
                <p className="text-gray-700">Upload national or subnational cold chain inventory files, map your columns to our canonical schema, validate, and submit for processing. Below is a summary of countries that have completed uploads.</p>
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">Select Country</label>
                <CountryPicker
                  value={selectedCountryOption}
                  onChange={(opt) => {
                    setSelectedCountryOption(opt);
                    const iso3 = opt?.iso3 || (typeof opt?.value === 'string' ? opt.value.toUpperCase() : '');
                    setSelectedIso3(String(iso3 || '').toUpperCase());
                  }}
                  placeholder="Select or search for country..."
                />
              </div>
              {!selectedIso3 && (
                <div className="px-3 py-3 border rounded bg-white">Please select a country to view the inventory summary.</div>
              )}
              {selectedIso3 && (
              <div className="mt-6">
                <div className="mb-2 text-sm text-gray-600">PQS categories and subcategories by country</div>
                {loading && (
                  <div className="px-3 py-3 border rounded bg-white">Loading…</div>
                )}
                {(!loading && error) && (
                  <div className="px-3 py-3 border rounded bg-white text-red-600">{error}</div>
                )}
                {!loading && !error && Object.keys(summary).length === 0 && (
                  <div className="px-3 py-3 border rounded bg-white">No data</div>
                )}
                {!loading && !error && Object.entries(summary).map(([iso3, rows]) => (
                  <div key={iso3} className="mb-6">
                    <div className="text-base font-semibold mb-2">{iso3}</div>
                    <div className="space-y-4">
                      {rows.map((r, idx) => {
                        const subKeys = ['WCR','WFR','TRC','CAR','IFR','REF','CRF','FRZ','VCX','CBX','RTM','ETM','VLR','GEN'];
                        const subRows = subKeys
                          .map(k => ({ code: k, value: r[k] ?? 0 }))
                          .filter(s => Number(s.value) > 0);
                        return (
                          <div key={`${r.pqs_category_code}-${idx}`} className="border rounded">
                            <div className="px-3 py-2 bg-gray-50 border-b flex items-center justify-between">
                              <div className="font-medium">{r.pqs_category_name} ({r.pqs_category_code})</div>
                              <div className="text-sm text-gray-600">Items: {r.items_count}</div>
                            </div>
                            <div className="px-3 py-2">
                              {subRows.length === 0 ? (
                                <div className="text-sm text-gray-500">No subcategories.</div>
                              ) : (
                                <div className="overflow-x-auto">
                                  <table className="min-w-full text-left">
                                    <thead>
                                      <tr>
                                        <th className="px-2 py-1 bg-gray-50 border">Subcategory</th>
                                        <th className="px-2 py-1 bg-gray-50 border w-24">Count</th>
                                      </tr>
                                    </thead>
                                    <tbody>
                                      {subRows.map(s => (
                                        <tr key={s.code}>
                                          <td className="px-2 py-1 border">{s.code}</td>
                                          <td className="px-2 py-1 border">{s.value}</td>
                                        </tr>
                                      ))}
                                    </tbody>
                                  </table>
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                ))}
              </div>
              )}
            </div>
          )}
          {activeTab === 'upload' && (
            <div>
              <UploadMappingWizard />
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Inventories; 